<template>
  <div class="tabs">
    <div class="tabs-header">
      <button v-show="showLeft" type="button" class="tabs-arrow tabs-arrow-left" @click="scroll(-1)">
        <i class="fa-solid fa-chevron-left"></i>
      </button>

      <div ref="nav" class="tabs-nav" @scroll="updateArrows">
        <button v-for="tab in tabs" :key="tab.key" type="button" class="tabs-tab" :class="{
          'tabs-tab-active': tab.key === activeKey,
          'tabs-tab-disabled': tab.disabled,
        }" :ref="'tab-' + tab.key" :disabled="tab.disabled" @click="select(tab)">
          <i v-if="tab.icon" :class="tab.icon" class="tabs-tab-icon"></i>
          <span class="tabs-tab-title">{{ tab.title }}</span>
          <span v-if="tab.badge !== undefined && tab.badge !== null && tab.badge !== ''" class="tabs-tab-badge">{{
            tab.badge }}</span>
        </button>
      </div>

      <button v-show="showRight" type="button" class="tabs-arrow tabs-arrow-right" @click="scroll(1)">
        <i class="fa-solid fa-chevron-right"></i>
      </button>
    </div>

    <div class="tabs-content">
      <div v-for="tab in tabs" v-show="tab.key === activeKey" :key="tab.key" class="tabs-panel">
        <slot :name="tab.key"></slot>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Tabs",
  props: {
    tabs: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      activeKey: null,
      showLeft: false,
      showRight: false,
      resizeObserver: null,
    };
  },
  created() {
    this.activeKey = this.resolveInitialKey();
  },
  mounted() {
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    if (this.$refs.nav) this.resizeObserver.observe(this.$refs.nav);
    window.addEventListener("resize", this.updateArrows);
    this.$nextTick(this.updateArrows);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener("resize", this.updateArrows);
  },
  methods: {
    resolveInitialKey() {
      if (this.value !== null && this.value !== undefined) return this.value;
      const first = this.tabs.find((tab) => !tab.disabled);
      return first ? first.key : null;
    },
    select(tab) {
      if (tab.disabled) return;
      this.activeKey = tab.key;
      this.$emit("input", tab.key);
      this.$emit("change", tab.key);
      this.$nextTick(() => {
        this.scrollActiveIntoView();
        this.updateArrows();
      });
    },
    scroll(direction) {
      const nav = this.$refs.nav;
      if (!nav) return;
      const step = Math.max(Math.floor(nav.clientWidth * 0.8), 200);
      nav.scrollBy({ left: step * direction, behavior: "smooth" });
    },
    scrollActiveIntoView() {
      const nav = this.$refs.nav;
      const ref = this.$refs["tab-" + this.activeKey];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (!nav || !el) return;
      const left = el.offsetLeft;
      const right = left + el.offsetWidth;
      if (left < nav.scrollLeft) {
        nav.scrollTo({ left: left - 16, behavior: "smooth" });
      } else if (right > nav.scrollLeft + nav.clientWidth) {
        nav.scrollTo({ left: right - nav.clientWidth + 16, behavior: "smooth" });
      }
    },
    updateArrows() {
      const nav = this.$refs.nav;
      if (!nav) return;
      const maxScroll = nav.scrollWidth - nav.clientWidth;
      this.showLeft = nav.scrollLeft > 1;
      this.showRight = nav.scrollLeft < maxScroll - 1;
    },
  },
  watch: {
    value(newValue) {
      if (newValue !== null && newValue !== undefined) {
        this.activeKey = newValue;
        this.$nextTick(this.scrollActiveIntoView);
      }
    },
    tabs() {
      this.$nextTick(this.updateArrows);
    },
  },
};
</script>

<style scoped>
.tabs {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.tabs-header {
  display: flex;
  align-items: stretch;
  position: relative;
  border-bottom: 0;
}

.tabs-nav {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 10px;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  scroll-behavior: smooth;
}

.tabs-nav::-webkit-scrollbar {
  height: 0;
  display: none;
}

.tabs-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex: 0 0 auto;
  min-width: 90px;
  white-space: nowrap;
  padding: 6px 14px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  font-size: 14px;
  cursor: pointer;
  letter-spacing: 2px;
  font-weight: 400;
  border-radius: 20px;
  transition: background 0.2s, color 0.2s;
}

.tabs-tab:hover:not(.tabs-tab-active):not(.tabs-tab-disabled) {
  background: var(--bg-hover);
  color: var(--text-primary);
}

.tabs-tab-active {
  color: var(--color-brand-text);
  background-color: var(--tabs-active-bg);
  font-weight: 500;
}

.tabs-tab-disabled {
  color: var(--text-muted);
  cursor: not-allowed;
  opacity: 0.6;
}

.tabs-tab-icon {
  font-size: 13px;
}

.tabs-tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--bg-elevated);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}

.tabs-tab-active .tabs-tab-badge {
  background: var(--color-brand);
  color: var(--text-inverse);
}

.tabs-arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 999px;
  background: var(--color-amber);
  color: var(--color-amber-text);
  font-size: 11px;
  cursor: pointer;
  transition: background 0.2s;
}

.tabs-arrow:hover {
  background: var(--color-amber-hover);
}

.tabs-arrow-left {
  margin-right: 6px;
}

.tabs-arrow-right {
  margin-left: 6px;
}

.tabs-content {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.tabs-panel {
  height: 100%;
}
</style>
